<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <login-layout>
    <oxd-text class="orangehrm-login-title" tag="h5">
      {{ $t('auth.login') }}
    </oxd-text>
    <div class="orangehrm-login-form">
      <div class="orangehrm-login-error">
        <oxd-alert
          :show="error !== null"
          :message="error?.message || ''"
          type="error"
        ></oxd-alert>
        <oxd-sheet
          v-if="isDemoMode"
          type="gray-lighten-2"
          class="orangehrm-demo-credentials"
        >
          <oxd-text tag="p">Username : Admin</oxd-text>
          <oxd-text tag="p">Password : admin123</oxd-text>
        </oxd-sheet>
      </div>
      <oxd-form
        ref="loginForm"
        method="post"
        :action="submitUrl"
        @submit-valid="onSubmit"
      >
        <input name="_token" :value="token" type="hidden" />

        <oxd-form-row>
          <oxd-input-field
            v-model="username"
            name="username"
            :label="$t('general.username')"
            label-icon="person"
            :placeholder="$t('auth.username')"
            :rules="rules.username"
            autofocus
          />
        </oxd-form-row>

        <oxd-form-row>
          <oxd-input-field
            v-model="password"
            name="password"
            :label="$t('general.password')"
            label-icon="key"
            :placeholder="$t('auth.password')"
            type="password"
            :rules="rules.password"
          />
        </oxd-form-row>

        <section class="orangehrm-beacon-config">
          <oxd-text tag="h6">Beacon configuration</oxd-text>
          <oxd-text tag="p" class="orangehrm-beacon-config-help">
            Select what will be initialized after this login. Secrets are kept
            on the server.
          </oxd-text>

          <oxd-form-row>
            <label class="orangehrm-beacon-label" for="beacon-config-select"
              >Saved setup</label
            >
            <select
              id="beacon-config-select"
              v-model="selectedConfigName"
              class="orangehrm-beacon-select"
              @change="handleConfigurationChange"
            >
              <option
                v-for="configuration in beaconConfigs"
                :key="configuration.name"
                :value="configuration.name"
              >
                {{ configuration.name }}
              </option>
            </select>
          </oxd-form-row>

          <label class="orangehrm-beacon-checkbox">
            <input v-model="configDraft.enabled" type="checkbox" />
            Load Beacon after login
          </label>
          <label class="orangehrm-beacon-checkbox">
            <input v-model="configDraft.includeAuthToken" type="checkbox" />
            Include authToken
          </label>
          <label class="orangehrm-beacon-checkbox">
            <input v-model="configDraft.includeMetadata" type="checkbox" />
            Include metaData
          </label>
          <label class="orangehrm-beacon-checkbox">
            <input v-model="configDraft.includeDomains" type="checkbox" />
            Include domains
          </label>

          <div class="orangehrm-beacon-fields">
            <oxd-input-field v-model="configDraft.sdkUrl" label="SDK URL" />
            <oxd-input-field
              v-model="configDraft.orgId"
              label="Organization ID"
            />
            <oxd-input-field
              v-model="configDraft.branchId"
              label="Branch ID (optional)"
            />
            <oxd-input-field
              v-model="configDraft.clientId"
              label="Client ID (optional)"
            />
            <oxd-input-field
              v-if="configDraft.includeDomains"
              v-model="configDraft.cdnUrl"
              label="CDN domain"
            />
            <oxd-input-field
              v-if="configDraft.includeDomains"
              v-model="configDraft.apiUrl"
              label="API domain"
            />
          </div>

          <div class="orangehrm-beacon-overrides">
            <label class="orangehrm-beacon-label" for="beacon-ui-overrides">
              UI config overrides (optional JSON)
            </label>
            <textarea
              id="beacon-ui-overrides"
              v-model="configDraft.uiConfigOverrides"
              class="orangehrm-beacon-textarea"
              rows="8"
              placeholder='{"colors":{"primary":"#FF0000"}}'
            ></textarea>
            <oxd-text
              v-if="configurationJsonError"
              tag="p"
              class="orangehrm-beacon-config-error"
            >
              {{ configurationJsonError }}
            </oxd-text>
          </div>

          <div
            v-if="configDraft.includeMetadata"
            class="orangehrm-beacon-metadata"
          >
            <oxd-text tag="p">Metadata fields</oxd-text>
            <div
              v-for="(field, index) in configDraft.metadata"
              :key="field.id"
              class="orangehrm-beacon-metadata-row"
            >
              <input
                v-model="field.key"
                class="orangehrm-beacon-input"
                placeholder="Key"
              />
              <input
                v-model="field.value"
                class="orangehrm-beacon-input"
                placeholder="Value"
              />
              <button
                type="button"
                class="orangehrm-beacon-remove"
                @click="removeMetadataField(index)"
              >
                Remove
              </button>
            </div>
            <button
              type="button"
              class="orangehrm-beacon-secondary"
              @click="addMetadataField"
            >
              Add field
            </button>
          </div>

          <div class="orangehrm-beacon-config-actions">
            <input
              v-model="configurationNameInput"
              class="orangehrm-beacon-input"
              placeholder="Configuration name"
            />
            <button
              type="button"
              class="orangehrm-beacon-secondary"
              @click="saveAsNewConfiguration"
            >
              Save as new
            </button>
            <button
              type="button"
              class="orangehrm-beacon-secondary"
              @click="updateConfiguration"
            >
              Update selected
            </button>
          </div>

          <oxd-text tag="p" class="orangehrm-beacon-preview">
            Injecting: {{ configurationSummary }}
          </oxd-text>
        </section>

        <oxd-form-actions class="orangehrm-login-action">
          <oxd-button
            class="orangehrm-login-button"
            display-type="main"
            :label="$t('auth.login')"
            type="submit"
          />
        </oxd-form-actions>
        <div class="orangehrm-login-forgot">
          <oxd-text class="orangehrm-login-forgot-header" @click="navigateUrl">
            {{ $t('auth.forgot_password') }}?
          </oxd-text>
        </div>
      </oxd-form>
      <template v-if="authenticators.length > 0">
        <oxd-divider class="orangehrm-login-seperator"></oxd-divider>
        <social-media-auth :authenticators="authenticators"></social-media-auth>
      </template>
    </div>
    <div class="orangehrm-login-footer">
      <div v-if="showSocialMedia" class="orangehrm-login-footer-sm">
        <a href="https://www.linkedin.com/company/beacon-li/" target="_blank">
          <oxd-icon type="svg" class="orangehrm-sm-icon" name="linkedinFill" />
        </a>
        <a href="https://www.facebook.com/beacon.li/" target="_blank">
          <oxd-icon type="svg" class="orangehrm-sm-icon" name="facebookFill" />
        </a>
        <a href="https://twitter.com/beacon_li" target="_blank">
          <oxd-icon type="svg" class="orangehrm-sm-icon" name="twitterFill" />
        </a>
        <a href="https://www.youtube.com/@beacon.li" target="_blank">
          <oxd-icon type="svg" class="orangehrm-sm-icon" name="youtubeFill" />
        </a>
      </div>
      <slot name="footer"></slot>
    </div>
  </login-layout>
</template>

<script>
import {urlFor} from '@ohrm/core/util/helper/url';
import {OxdAlert, OxdIcon, OxdSheet} from '@ohrm/oxd';
import {required} from '@ohrm/core/util/validation/rules';
import {navigate, reloadPage} from '@ohrm/core/util/helper/navigation';
import LoginLayout from '@/orangehrmAuthenticationPlugin/components/LoginLayout.vue';
import SocialMediaAuth from '@/orangehrmAuthenticationPlugin/components/SocialMediaAuth.vue';

export default {
  components: {
    'oxd-icon': OxdIcon,
    'oxd-alert': OxdAlert,
    'oxd-sheet': OxdSheet,
    'login-layout': LoginLayout,
    'social-media-auth': SocialMediaAuth,
  },

  props: {
    error: {
      type: Object,
      default: () => null,
    },
    token: {
      type: String,
      required: true,
    },
    showSocialMedia: {
      type: Boolean,
      default: true,
    },
    isDemoMode: {
      type: Boolean,
      default: false,
    },
    authenticators: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      username: '',
      password: '',
      rules: {
        username: [required],
        password: [required],
      },
      submitted: false,
      beaconConfigs: [],
      selectedConfigName: 'Default-bkn',
      configurationNameInput: '',
      configDraft: null,
    };
  },

  created() {
    this.loadBeaconConfigurations();
  },

  computed: {
    submitUrl() {
      return urlFor('/auth/validate');
    },
    configurationSummary() {
      if (!this.configDraft) return 'Default-bkn';
      const sections = ['SDK URL', 'organization ID'];
      if (this.configDraft.includeAuthToken) sections.push('authToken');
      if (this.configDraft.includeMetadata) sections.push('metaData');
      if (this.configDraft.includeDomains) sections.push('domains');
      if (this.configDraft.branchId) sections.push('branchId');
      if (this.configDraft.clientId) sections.push('clientId');
      if (this.configDraft.uiConfigOverrides.trim())
        sections.push('uiConfigOverrides');
      return sections.join(', ');
    },
    configurationJsonError() {
      if (!this.configDraft || !this.configDraft.uiConfigOverrides.trim())
        return '';
      try {
        const parsed = JSON.parse(this.configDraft.uiConfigOverrides);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
          ? ''
          : 'UI config overrides must be a JSON object.';
      } catch (error) {
        return 'UI config overrides must contain valid JSON.';
      }
    },
  },

  beforeMount() {
    setTimeout(() => {
      reloadPage();
    }, 1200000); // 20 * 60 * 1000 (20 minutes);
  },

  methods: {
    onSubmit() {
      if (!this.submitted) {
        if (this.configurationJsonError) return;
        this.persistConfiguration(this.selectedConfigName, this.configDraft);
        window.localStorage.setItem(
          'beacon.pendingConfiguration',
          JSON.stringify({
            ...this.configDraft,
            username: this.username,
          }),
        );
        this.submitted = true;
        this.$refs.loginForm.$el.submit();
      }
    },
    defaultBeaconConfiguration(name = 'Default-bkn') {
      return {
        name,
        enabled: true,
        includeAuthToken: false,
        includeMetadata: false,
        includeDomains: false,
        sdkUrl: 'https://cdn.beacon.li/sdk/latest.js',
        orgId: 'CFZHNX',
        branchId: '',
        clientId: '',
        uiConfigOverrides: '',
        cdnUrl: 'cdn.beacon.li',
        apiUrl: 'spyder.beacon.li',
        metadata: [
          {id: `${Date.now()}-shardKey1`, key: 'shardKey1', value: ''},
          {id: `${Date.now()}-shardKey2`, key: 'shardKey2', value: ''},
          {id: `${Date.now()}-shardKey3`, key: 'shardKey3', value: ''},
          {id: `${Date.now()}-times`, key: 'times', value: ''},
          {id: `${Date.now()}-language`, key: 'language', value: ''},
        ],
      };
    },
    loadBeaconConfigurations() {
      let configurations = [];
      try {
        configurations = JSON.parse(
          window.localStorage.getItem('beacon.configurations') || '[]',
        );
      } catch (error) {
        configurations = [];
      }
      this.beaconConfigs =
        Array.isArray(configurations) && configurations.length > 0
          ? configurations.map((configuration) =>
              this.normalizeConfiguration(configuration),
            )
          : [this.defaultBeaconConfiguration()];
      window.localStorage.setItem(
        'beacon.configurations',
        JSON.stringify(this.beaconConfigs),
      );
      this.selectedConfigName =
        window.localStorage.getItem('beacon.selectedConfiguration') ||
        'Default-bkn';
      if (
        !this.beaconConfigs.some(
          (configuration) => configuration.name === this.selectedConfigName,
        )
      ) {
        this.selectedConfigName = this.beaconConfigs[0].name;
      }
      this.loadSelectedConfiguration();
    },
    loadSelectedConfiguration() {
      const selected = this.beaconConfigs.find(
        (configuration) => configuration.name === this.selectedConfigName,
      );
      this.configDraft = this.normalizeConfiguration(
        selected || this.defaultBeaconConfiguration(),
      );
      this.configurationNameInput = this.configDraft.name;
      window.localStorage.setItem(
        'beacon.selectedConfiguration',
        this.selectedConfigName,
      );
    },
    handleConfigurationChange() {
      this.loadSelectedConfiguration();
    },
    normalizeConfiguration(configuration) {
      const defaults = this.defaultBeaconConfiguration(
        configuration?.name || 'Default-bkn',
      );
      const savedMetadata = Array.isArray(configuration?.metadata)
        ? configuration.metadata
        : [];
      const defaultMetadata = defaults.metadata.filter(
        (defaultField) =>
          !savedMetadata.some((field) => field.key === defaultField.key),
      );
      const normalized = {
        ...defaults,
        ...configuration,
        metadata: [...savedMetadata, ...defaultMetadata].map((field) => ({
          id: field.id || `${Date.now()}-${Math.random()}`,
          key: field.key || '',
          value: field.value || '',
        })),
      };
      normalized.includeMetadata = configuration?.includeMetadata === true;
      normalized.includeAuthToken = configuration?.includeAuthToken === true;
      normalized.includeDomains = configuration?.includeDomains === true;
      normalized.enabled = configuration?.enabled !== false;
      normalized.sdkUrl = configuration?.sdkUrl || defaults.sdkUrl;
      normalized.orgId = configuration?.orgId || defaults.orgId;
      normalized.branchId = configuration?.branchId || '';
      normalized.clientId = configuration?.clientId || '';
      normalized.uiConfigOverrides = configuration?.uiConfigOverrides || '';
      delete normalized.includeShareKeys;
      return normalized;
    },
    persistConfiguration(name, configuration) {
      const savedConfiguration = JSON.parse(
        JSON.stringify({...configuration, name}),
      );
      const index = this.beaconConfigs.findIndex((item) => item.name === name);
      if (index === -1) this.beaconConfigs.push(savedConfiguration);
      else this.beaconConfigs.splice(index, 1, savedConfiguration);
      window.localStorage.setItem(
        'beacon.configurations',
        JSON.stringify(this.beaconConfigs),
      );
      window.localStorage.setItem('beacon.selectedConfiguration', name);
    },
    updateConfiguration() {
      if (this.configurationJsonError) return;
      this.persistConfiguration(this.selectedConfigName, this.configDraft);
      this.loadBeaconConfigurations();
    },
    saveAsNewConfiguration() {
      const name = this.configurationNameInput.trim();
      if (!name || this.configurationJsonError) return;
      this.persistConfiguration(name, this.configDraft);
      this.selectedConfigName = name;
      this.loadBeaconConfigurations();
    },
    addMetadataField() {
      this.configDraft.metadata.push({
        id: `${Date.now()}-${Math.random()}`,
        key: '',
        value: '',
      });
    },
    removeMetadataField(index) {
      this.configDraft.metadata.splice(index, 1);
    },
    navigateUrl() {
      navigate('/auth/requestPasswordResetCode');
    },
  },
};
</script>

<style src="./login.scss" lang="scss" scoped></style>
